import { TournamentStatus, TournamentStyle, TournamentVisibility } from "../../../generated/bpong_pb";
import type { TFunction } from "i18next";

const tagColors = [
    "magenta",
    "red",
    "volcano",
    "orange",
    "gold",
    "lime",
    "green",
    "cyan",
    "blue",
    "geekblue",
    "purple",
];

export const getOriginColor = (origin: string): string => {
    let hash = 0;
    for (let i = 0; i < origin.length; i++) {
        const codePoint = origin.codePointAt(i)!;
        const codeUnit = codePoint > 0xffff ? 0xd800 + ((codePoint - 0x10000) >> 10) : codePoint;
        hash = codeUnit + ((hash << 5) - hash);
    }
    const idx = Math.abs(hash) % tagColors.length;
    return tagColors[idx];
};

const STYLE_OPTION_DEFS = [
    {
        value: TournamentStyle.TOURNAMENT_STYLE_ROUND_ROBIN,
        labelKey: "beerpong.tournament.styles.roundRobin",
    },
    {
        value: TournamentStyle.TOURNAMENT_STYLE_SINGLE_ELIMINATION,
        labelKey: "beerpong.tournament.styles.singleElimination",
    },
    {
        value: TournamentStyle.TOURNAMENT_STYLE_DOUBLE_ELIMINATION,
        labelKey: "beerpong.tournament.styles.doubleElimination",
    },
    {
        value: TournamentStyle.TOURNAMENT_STYLE_HYBRID,
        labelKey: "beerpong.tournament.styles.hybrid",
    },
];

export const buildStyleOptions = (t: TFunction) =>
    STYLE_OPTION_DEFS.map((option) => ({
        value: option.value,
        label: t(option.labelKey),
    }));

export const DEFAULT_STYLE = TournamentStyle.TOURNAMENT_STYLE_SINGLE_ELIMINATION;

export const styleLabel = (style: TournamentStyle, t: TFunction): string => {
    const option = STYLE_OPTION_DEFS.find((opt) => opt.value === style) ?? STYLE_OPTION_DEFS[0];
    return t(option.labelKey);
};

const STATUS_META_DEFS = [
    {
        status: TournamentStatus.TOURNAMENT_STATUS_ACTIVE,
        color: "green",
        labelKey: "beerpong.tournament.status.active",
    },
    {
        status: TournamentStatus.TOURNAMENT_STATUS_COMPLETED,
        color: "blue",
        labelKey: "beerpong.tournament.status.completed",
    },
    {
        status: TournamentStatus.TOURNAMENT_STATUS_CANCELLED,
        color: "red",
        labelKey: "beerpong.tournament.status.cancelled",
    },
];

export const buildStatusMeta = (t: TFunction) =>
    new Map<TournamentStatus, { color: string; label: string }>(
        STATUS_META_DEFS.map(({ status, color, labelKey }) => [status, { color, label: t(labelKey) }])
    );

const VISIBILITY_OPTION_DEFS = [
    {
        value: TournamentVisibility.TOURNAMENT_VISIBILITY_PUBLIC,
        color: "green",
        labelKey: "beerpong.tournament.visibility.public",
    },
    {
        value: TournamentVisibility.TOURNAMENT_VISIBILITY_FRIENDS,
        color: "blue",
        labelKey: "beerpong.tournament.visibility.friends",
    },
    {
        value: TournamentVisibility.TOURNAMENT_VISIBILITY_PASSWORD,
        color: "orange",
        labelKey: "beerpong.tournament.visibility.password",
    },
    {
        value: TournamentVisibility.TOURNAMENT_VISIBILITY_PRIVATE,
        color: "red",
        labelKey: "beerpong.tournament.visibility.private",
    },
];

export const buildVisibilityOptions = (t: TFunction) =>
    VISIBILITY_OPTION_DEFS.map((option) => ({
        value: option.value,
        label: t(option.labelKey),
        color: option.color,
    }));

export const visibilityLabel = (visibility: TournamentVisibility, t: TFunction): string => {
    const option = VISIBILITY_OPTION_DEFS.find((opt) => opt.value === visibility) ?? VISIBILITY_OPTION_DEFS[0];
    return t(option.labelKey);
};

export const isEliminationStyle = (style: TournamentStyle): boolean =>
    style !== TournamentStyle.TOURNAMENT_STYLE_ROUND_ROBIN;

export const isDoubleEliminationStyle = (style: TournamentStyle): boolean =>
    style === TournamentStyle.TOURNAMENT_STYLE_DOUBLE_ELIMINATION ||
    style === TournamentStyle.TOURNAMENT_STYLE_HYBRID;
